// ============================================================================
// CASE NUMBER FORMAT SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { caseNumberFormatEndpoints, CASE_NUMBER_FORMAT_BASE_PATH } from '../endpoints/caseNumberFormat.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  CaseNumberFormat,
  CaseNumberFormatListItem,
  CreateCaseNumberFormatRequest,
  UpdateCaseNumberFormatRequest,
  CaseNumberFormatSearchRequest,
} from '../types/caseNumberFormat.types';

class CaseNumberFormatService {
  async getAll(params?: CaseNumberFormatSearchRequest): Promise<PaginatedResponse<CaseNumberFormatListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(CASE_NUMBER_FORMAT_BASE_PATH, params)
      : caseNumberFormatEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<CaseNumberFormat>> {
    const response = await get<ApiResponse<CaseNumberFormat>>(caseNumberFormatEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<CaseNumberFormat>> {
    const response = await get<ApiResponse<CaseNumberFormat>>(caseNumberFormatEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateCaseNumberFormatRequest): Promise<ApiResponse<CaseNumberFormat>> {
    const response = await post<ApiResponse<CaseNumberFormat>>(caseNumberFormatEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCaseNumberFormatRequest): Promise<ApiResponse<CaseNumberFormat>> {
    const response = await put<ApiResponse<CaseNumberFormat>>(caseNumberFormatEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(caseNumberFormatEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${caseNumberFormatEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : caseNumberFormatEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const caseNumberFormatService = new CaseNumberFormatService();
export default caseNumberFormatService;
